import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { hotelCategoryLabel, hotelImageUrls, hotelPlaceLabel } from './previewUtils';

function hotelPreviewSubtitle(hotel, t) {
  const place = hotelPlaceLabel(hotel);
  const category = String(hotel.hotel?.category || '')
    .split('★')
    .join('')
    .trim();
  if (place && category) {
    return t('packages.preview.hotelCategorySubtitle', { place, category });
  }
  if (category) {
    return t('packages.preview.hotelCategoryOnly', { category });
  }
  return place;
}

function HotelPreviewModal({ hotel, variant, images, imageIndex, description, onClose, t }) {
  const [activeIndex, setActiveIndex] = useState(imageIndex);
  const isGallery = variant === 'gallery';
  const subtitle = isGallery ? '' : hotelPreviewSubtitle(hotel, t);
  const closeLabel = t('footer.close');
  const dialogClass = isGallery
    ? 'pkg-hotel-preview-dialog'
    : 'pkg-hotel-preview-dialog pkg-hotel-preview-dialog--text';

  useEffect(() => {
    setActiveIndex(imageIndex);
  }, [imageIndex]);

  useEffect(() => {
    function onKeyDown(event) {
      if (event.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [onClose]);

  return createPortal(
    <div className='pkg-hotel-preview-portal'>
      <button
        type='button'
        className='pkg-hotel-preview-backdrop'
        aria-label={closeLabel}
        onClick={onClose}
      />
      <dialog className={dialogClass} open aria-labelledby='hotel-preview-title'>
        <div className='pkg-hotel-preview-head'>
          <div>
            <h2 id='hotel-preview-title'>{hotel.hotel?.name || hotel.name}</h2>
            {subtitle ? <p className='pkg-hotel-preview-sub'>{subtitle}</p> : null}
          </div>
          <button
            className='pkg-hotel-preview-close'
            type='button'
            onClick={onClose}
            title={closeLabel}
            aria-label={closeLabel}
          >
            ×
          </button>
        </div>
        {isGallery ? (
          <div className='pkg-hotel-preview-gallery'>
            <div className='pkg-hotel-preview-photo'>
              <img src={images[activeIndex]} alt='' />
            </div>
            {images.length > 1 ? (
              <div className='pkg-hotel-preview-thumbs' role='tablist'>
                {images.map((src, index) => (
                  <button
                    key={src}
                    className={
                      index === activeIndex
                        ? 'pkg-hotel-preview-thumb is-active'
                        : 'pkg-hotel-preview-thumb'
                    }
                    type='button'
                    role='tab'
                    aria-selected={index === activeIndex}
                    onClick={() => setActiveIndex(index)}
                  >
                    <img src={src} alt='' />
                  </button>
                ))}
              </div>
            ) : null}
          </div>
        ) : (
          <div className='pkg-hotel-preview-text'>{description}</div>
        )}
      </dialog>
    </div>,
    document.body
  );
}

export default function HotelPreviewCard({ hotel }) {
  const { t } = useTranslation('layout');
  const images = hotelImageUrls(hotel.hotel);
  const [modal, setModal] = useState(null);
  const description = (hotel.hotel?.description || '').trim();
  const place = hotelPlaceLabel(hotel);
  const stars = hotelCategoryLabel(hotel.hotel?.category);
  const nights = Number(hotel.totalNights);
  const nightsLabel = nights
    ? t('packages.preview.hotelNights', { count: nights })
    : '—';

  return (
    <div className='preview-item-card'>
      <div className='hotel-card-head'>
        <div className='hotel-card-title'>
          <span className='hotel-card-heading'>
            <strong>{hotel.hotel?.name || hotel.name}</strong>
            {place ? <span className='hotel-card-place'> — {place}</span> : null}
          </span>
          {stars ? <span className='hotel-card-stars'>{stars}</span> : null}
        </div>
        {description ? (
          <button
            className='hotel-desc-link'
            type='button'
            onClick={() => setModal({ variant: 'description' })}
          >
            <svg viewBox='0 0 24 24' aria-hidden='true'>
              <circle cx='12' cy='12' r='9' />
              <path d='M12 11v6M12 8h.01' />
            </svg>
            {t('packages.details.description')}
          </button>
        ) : null}
      </div>
      <div className='hotel-card-meta'>
        {hotel.roomType || '—'} · {hotel.regime || '—'} · {nightsLabel}
      </div>
      {images.length > 0 && (
        <div className='hotel-gallery'>
          <div className='hotel-thumb-row'>
            {images.map((src, index) => (
              <button
                key={src}
                className='hotel-thumb'
                type='button'
                onClick={() => setModal({ variant: 'gallery', imageIndex: index })}
              >
                <img src={src} alt='' />
              </button>
            ))}
          </div>
        </div>
      )}
      {modal ? (
        <HotelPreviewModal
          hotel={hotel}
          variant={modal.variant}
          images={images}
          imageIndex={modal.imageIndex || 0}
          description={description}
          onClose={() => setModal(null)}
          t={t}
        />
      ) : null}
    </div>
  );
}
